/**
 * Row-details panel (top of the Single-Row Details View).
 *
 * Layout:
 *   - `flex-1`: takes all remaining vertical space above the Related Data panel.
 *   - `RowDetailsToolbar` is sticky at the top.
 *   - Cells are grouped into categories via `useCellCategorization`.
 *   - A draggable divider below the panel separates it from `RelatedDataPanel`.
 *
 * Slice 2 wires all 4 toolbar toggles (search filter, hide-nulls, group,
 * density) + `feature:single_row_toolbar_toggle` analytics for each — the
 * analytics wiring lives in `useToolbarAnalytics` to keep this file lean.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RowDetailsToolbar } from './RowDetailsToolbar';
import { RowCellsGroup } from './RowCellsGroup';
import { useCellCategorization } from '../hooks/useCellCategorization';
import { useToolbarAnalytics } from '../hooks/useToolbarAnalytics';
import { useSingleRowStore, selectVisit, buildRowVisitKey } from '../store/singleRowStore';
import { useAppStore } from '../../../../store/appStore';
import { useMetaInfo } from '../../common/hooks/useMetaInfo';
import { categorizeEnumColumn } from '../../common/utils/enumColumnCategorization';
import { resolveLookupEnumTarget } from '../../common/utils/lookupEnumTarget';
import {
  isOnDemandLookupColumn,
  resolveEffectiveLookupDeclaration,
} from '../../common/utils/effectiveLookupDeclaration';
import { categorizeSpatialColumn } from '../../common/utils/spatialColumnCategorization';
import { engineSupports } from '../../../../core/engines/engineCapabilities';
import { SpatialRowGroupMap } from './SpatialRowGroupMap';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { RowCellGroup } from '../types/singleRowTypes';
import type { RowCellSmartEditContext } from './RowCell';
import type { DatabaseEngineId } from '../../../../core/engines';
import { lookupForeignKey, type ForeignKeyMap } from '../../table-explorer/hooks/useTableMetadata';

const WIDE_ROW_THRESHOLD = 30;

type RowDetailsPanelProps = {
  readonly schema: string;
  readonly table: string;
  readonly pkHash: string;
  readonly row: Record<string, unknown>;
  readonly columns: ReadonlyArray<TableExplorerColumn>;
  /** Slice 3 (EP4) — bubbled from RowCell. */
  readonly onFkClick?: (args: { column: string; value: unknown; modifierKey: boolean }) => void;
  /** When true, each cell renders an Edit (Pencil) affordance on hover. */
  readonly editable?: boolean;
  /** Connection + database identifiers for smart-edit pickers. Required when editable. */
  readonly connectionId?: string;
  readonly database?: string;
  /** Engine id used by smart-edit pickers. Required when editable. */
  readonly engine?: DatabaseEngineId;
  /** Real-FK lookup keyed by column name (normalized). */
  readonly foreignKeyMap?: ForeignKeyMap;
  /** Called when the user commits a cell edit. Receives column, oldValue, newValue. */
  readonly onEditCommit?: (args: { column: string; oldValue: unknown; newValue: unknown }) => void;
};

function countNullCells(groups: ReadonlyArray<RowCellGroup>): number {
  let n = 0;
  for (const g of groups) {
    for (const c of g.cells) {
      if (c.value === null || c.value === undefined) n += 1;
    }
  }
  return n;
}

export function RowDetailsPanel({
  schema,
  table,
  pkHash,
  row,
  columns,
  onFkClick,
  editable = false,
  connectionId,
  database,
  engine,
  foreignKeyMap,
  onEditCommit,
}: RowDetailsPanelProps) {
  const rowKey = useMemo(
    () => buildRowVisitKey({ schema, table, pkHash }),
    [schema, table, pkHash],
  );
  const visit = useSingleRowStore((state) => selectVisit(state, rowKey));
  const toggleGroupCollapsed = useSingleRowStore((state) => state.toggleGroupCollapsed);

  // Native-spatial column names → the `spatial` category (+ its mini map). Native
  // arm only (T4/`categorizeSpatialColumn`); the rules file stays engine-agnostic.
  const spatialColumnNames = useMemo(() => {
    const names = new Set<string>();
    if (!engine || !engineSupports(engine, 'spatial', 'supportsSpatialTypes')) return names;
    for (const col of columns) {
      if (
        categorizeSpatialColumn(
          { engine, schema, table, name: col.name, dataType: col.dataType ?? '' },
          null,
        ) === 'native-spatial'
      ) {
        names.add(col.name);
      }
    }
    return names;
  }, [engine, schema, table, columns]);

  const groups = useCellCategorization({ columns: columns as TableExplorerColumn[], row, spatialColumnNames });
  const totalColumns = columns.length;
  const nullCellCount = useMemo(() => countNullCells(groups), [groups]);

  // Toolbar prefs persist across visits via appStore. `hideNulls` keeps its
  // wide-row auto-default until the user explicitly toggles it (undefined =
  // unset). Cell search remains per-visit only.
  const persistedHideNulls = useAppStore((s) => s.rowDetailsHideNulls);
  const density = useAppStore((s) => s.rowDetailsDensity);
  const groupByCategory = useAppStore((s) => s.rowDetailsGroupByCategory);
  const setPersistedHideNulls = useAppStore((s) => s.setRowDetailsHideNulls);
  const setPersistedDensity = useAppStore((s) => s.setRowDetailsDensity);
  const setPersistedGroupByCategory = useAppStore((s) => s.setRowDetailsGroupByCategory);

  const hideNulls =
    persistedHideNulls === undefined
      ? totalColumns > WIDE_ROW_THRESHOLD
      : persistedHideNulls;

  const [cellSearch, setCellSearch] = useState('');

  // Cell search resets per row visit — it's a per-row filter, not a pref.
  useEffect(() => {
    setCellSearch('');
  }, [rowKey]);

  // useToolbarAnalytics expects `(prev) => next` updater setters. Adapt the
  // direct-value zustand setters to that shape so analytics still fire.
  const setHideNulls = useCallback(
    (updater: (prev: boolean) => boolean) => setPersistedHideNulls(updater(hideNulls)),
    [hideNulls, setPersistedHideNulls],
  );
  const setDensity = useCallback(
    (updater: (prev: 'compact' | 'normal') => 'compact' | 'normal') =>
      setPersistedDensity(updater(density)),
    [density, setPersistedDensity],
  );
  const setGroupByCategory = useCallback(
    (updater: (prev: boolean) => boolean) =>
      setPersistedGroupByCategory(updater(groupByCategory)),
    [groupByCategory, setPersistedGroupByCategory],
  );

  // Smart-edit context resolver for FK / enum / boolean variants. Pulls
  // MetaInfo once per render and exposes a `(columnName) => context` closure
  // that RowCellsGroup hands to each RowCell.
  const { data: metaInfo } = useMetaInfo(connectionId ?? '', database ?? '');
  const columnByName = useMemo(() => {
    const m = new Map<string, TableExplorerColumn>();
    for (const c of columns) m.set(c.name, c);
    return m;
  }, [columns]);
  const resolveSmartEditContext = useCallback(
    (columnName: string): RowCellSmartEditContext | undefined => {
      if (!editable || !connectionId || !database || !engine) return undefined;
      const col = columnByName.get(columnName);
      if (!col) return undefined;
      const cat = categorizeEnumColumn(
        {
          engine,
          schema,
          table,
          name: col.name,
          dataType: col.dataType,
          isNativeEnumType: col.isNativeEnumType,
        },
        metaInfo,
      );
      const enumNative = cat === 'native-enum';
      // By the exact column name (`lookupForeignKey`): `"ref id"` never takes
      // `"ref-id"`'s FK; another case still finds its one column.
      const realFk = lookupForeignKey(foreignKeyMap, col.name);
      const realFkTarget = realFk && !realFk.isLoose
        ? {
            schema: realFk.referencedSchema ?? '',
            table: realFk.referencedTable,
            column: realFk.referencedColumn,
          }
        : undefined;
      const looseRelCandidates = (metaInfo?.looseRelationships ?? []).filter(
        (rel) =>
          rel.fromSchema === schema &&
          rel.fromTable === table &&
          rel.fromColumn === col.name &&
          !rel.sourcePath,
      );
      // An *implicit* lookup enum has no declaration of its own, so
      // `categorizeEnumColumn` can't see it: what makes it one is the
      // reference-table flag on the table its FK points at. Same seam and same
      // outcome as both grids — the labelled enum editor with the row-picker
      // escape (two-grids parity: `ResultsGrid`'s row side panel).
      const lookupInput = {
        metaInfo,
        schema,
        table,
        column: col.name,
        resolvedTarget: resolveLookupEnumTarget({
          dataType: col.dataType,
          realFk:
            realFk && !realFk.isLoose
              ? {
                  referencedSchema: realFk.referencedSchema,
                  referencedTable: realFk.referencedTable,
                  referencedColumn: realFk.referencedColumn,
                }
              : undefined,
          looseRels: looseRelCandidates,
        }).target,
      };
      const implicitLookup =
        resolveEffectiveLookupDeclaration(lookupInput)?.kind === 'implicit';
      // …but an on-demand target has no pair list to put in an editor, whether
      // the column is implicit or explicitly declared. Same exclusion both
      // grids make on their declared-LIKE set: those columns fall through to
      // the FK row picker.
      const enumDeclared =
        (cat === 'declared-enum' || implicitLookup) && !isOnDemandLookupColumn(lookupInput);
      return {
        connectionId,
        database,
        engine,
        schema,
        table,
        realFkTarget,
        looseRelCandidates,
        enumDeclared,
        enumNative,
        sourceRow: row,
      };
    },
    [editable, connectionId, database, engine, columnByName, schema, table, metaInfo, foreignKeyMap, row],
  );

  const handlers = useToolbarAnalytics({
    setHideNulls,
    setDensity,
    setGroupByCategory,
    setCellSearch,
  });

  // Apply search + null filters. Search matches the column name
  // (case-insensitive substring). Empty groups are dropped so headers don't
  // linger when all cells filter out.
  const normalizedSearch = cellSearch.trim().toLowerCase();
  const filteredGroups = useMemo<ReadonlyArray<RowCellGroup>>(() => {
    return groups
      .map((g) => ({
        ...g,
        cells: g.cells.filter((c) => {
          if (hideNulls && (c.value === null || c.value === undefined)) return false;
          if (
            normalizedSearch.length > 0 &&
            !c.column.name.toLowerCase().includes(normalizedSearch)
          ) {
            return false;
          }
          return true;
        }),
      }))
      .filter((g) => g.cells.length > 0);
  }, [groups, hideNulls, normalizedSearch]);

  // Group-by-category off → render a single synthetic "All" group.
  const renderedGroups = useMemo<ReadonlyArray<RowCellGroup>>(() => {
    if (groupByCategory) return filteredGroups;
    const allCells = filteredGroups.flatMap((g) => g.cells);
    if (allCells.length === 0) return [];
    return [{ key: 'metadata', label: 'All', cells: allCells }];
  }, [filteredGroups, groupByCategory]);

  return (
    <section
      className="flex-1 min-h-0 flex flex-col overflow-hidden bg-muted/10"
      style={{ minHeight: '120px' }}
      data-testid="single-row-details-panel"
      data-density={density}
    >
      <RowDetailsToolbar
        state={{ cellSearch, hideNulls, nullCellCount, density, groupByCategory }}
        onCellSearchChange={handlers.onCellSearchChange}
        onToggleHideNulls={handlers.onToggleHideNulls}
        onToggleDensity={handlers.onToggleDensity}
        onToggleGroupByCategory={handlers.onToggleGroupByCategory}
      />
      <div
        className="flex-1 min-h-0 overflow-y-auto p-2 flex flex-col gap-2"
        data-testid="row-details-scroll"
      >
        {renderedGroups.length === 0 ? (
          <div className="text-xs text-muted-foreground py-4 text-center">
            No cells to display.
          </div>
        ) : (
          renderedGroups.map((g) => (
            <RowCellsGroup
              key={g.key}
              group={g}
              density={density}
              collapsed={visit.collapsedGroups.includes(g.key)}
              onToggleCollapsed={() => toggleGroupCollapsed(rowKey, g.key)}
              onFkClick={onFkClick}
              editable={editable}
              onEditCommit={onEditCommit}
              resolveSmartEditContext={editable ? resolveSmartEditContext : undefined}
              mapSlot={
                g.key === 'spatial' && engine ? (
                  <SpatialRowGroupMap
                    engine={engine}
                    cells={g.cells.map((c) => ({
                      name: c.column.name,
                      value: c.value,
                      dataType: c.column.dataType,
                    }))}
                  />
                ) : undefined
              }
            />
          ))
        )}
      </div>
    </section>
  );
}
